import { deleteApi, getApi, patchApi, postApi, putApi } from "../helpers/auth_helpers";

const base = process.env.REACT_APP_SERVERURL || "";

export const pieceQrLanding = (productUniqueId: string) =>
  getApi(`${base}/public/piece-qr/${encodeURIComponent(productUniqueId)}`);

export const lookupSalesProduct = (q: string) => {
  const query = new URLSearchParams();
  if (q) query.set("q", q);
  return getApi(`${base}/studio/sales/product-lookup?${query.toString()}`);
};

export const listSalesMemos = (params: {
  offset?: number;
  limit?: number;
  status?: string;
  type?: string;
  q?: string;
}) => {
  const query = new URLSearchParams();
  query.set("offset", String(params.offset ?? 0));
  query.set("limit", String(params.limit ?? 20));
  if (params.status) query.set("status", params.status);
  if (params.type) query.set("type", params.type);
  if (params.q) query.set("q", params.q);
  return getApi(`${base}/studio/sales/memos?${query.toString()}`);
};

export const getSalesMemo = (id: string) => getApi(`${base}/studio/sales/memos/${id}`);

export const createSalesMemo = (body: Record<string, unknown>) =>
  postApi(`${base}/studio/sales/memos`, body);

export const updateSalesMemo = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/studio/sales/memos/${id}`, body);

export const addSalesMemoLine = (id: string, productQuery: string) =>
  postApi(`${base}/studio/sales/memos/${id}/lines`, { productQuery });

export const addSalesMemoLinesFromSale = (id: string, sourceMemoId: string, lineIds: string[]) =>
  postApi(`${base}/studio/sales/memos/${id}/lines/from-sale`, { sourceMemoId, lineIds });

export const dropSalesMemoLine = (id: string, lineId: string) =>
  postApi(`${base}/studio/sales/memos/${id}/lines/${lineId}/drop`, {});

export const setSalesMemoLineHsn = (id: string, lineId: string, hsnCode: string) =>
  postApi(`${base}/studio/sales/memos/${id}/lines/${lineId}/hsn`, { hsnCode });

export const sendSalesMemo = (id: string) =>
  postApi(`${base}/studio/sales/memos/${id}/send`, {});

/** Approval memo (goods on approval): send out, mark pieces returned, keep the rest as a new sale memo. */
export const sendSalesApproval = (id: string) =>
  postApi(`${base}/studio/sales/memos/${id}/approval/send`, {});

export const returnSalesApprovalLines = (id: string, lineIds: string[]) =>
  postApi(`${base}/studio/sales/memos/${id}/approval/return`, { lineIds });

export const keepSalesApprovalRest = (id: string) =>
  postApi(`${base}/studio/sales/memos/${id}/approval/keep`, {});

export const reopenSalesMemo = (id: string) =>
  postApi(`${base}/studio/sales/memos/${id}/reopen`, {});

export const cancelSalesMemo = (id: string, cancelReason?: string) =>
  postApi(`${base}/studio/sales/memos/${id}/cancel`, { cancelReason });

export const confirmSalesBilled = (id: string, body: Record<string, unknown>) =>
  postApi(`${base}/studio/sales/memos/${id}/confirm-billed`, body);

export const confirmSalesCredit = (id: string, body: Record<string, unknown>) =>
  postApi(`${base}/studio/sales/memos/${id}/confirm-credit`, body);

/** Ready for dispatch / Shipped for every piece on a sale memo; each piece's scan journey shows it. */
export const markSalesDispatch = (id: string, body: { stage: "READY" | "SHIPPED"; date?: string; courier?: string; trackingNumber?: string }) =>
  postApi(`${base}/studio/sales/memos/${id}/dispatch`, body);

export const getSalesReversalContext = (q: string) =>
  getApi(`${base}/studio/sales/reversal-context?q=${encodeURIComponent(q)}`);

export const takeBackSalesProduct = (body: Record<string, unknown>) =>
  postApi(`${base}/studio/sales/take-back`, body);

export const getProductMovement = (productUniqueId: string) =>
  getApi(`${base}/studio/sales/products/${encodeURIComponent(productUniqueId)}/movement`);

// ---- Parcels on billed sale and approval memos (dispatch tracking) -------------------------------------------

export type ParcelStage = "BOOKED" | "IN_TRANSIT" | "OUT_FOR_DELIVERY" | "DELIVERED" | "RETURNED";

/** The server's envelope. `data` is null when `success` is false; `status` is the HTTP status. */
export type SalesApiResult<T> = { success: boolean; status?: number; message?: string; code?: string; data: T | null };

export type ParcelView = {
  id: string;
  memoId: string;
  memoCode: string;
  memoType: "SALE" | "APPROVAL";
  courierId: string;
  courierName: string;
  awbNumber: string;
  trackingUrl: string | null;
  /** yyyy-MM-dd */
  bookedOn: string;
  stage: ParcelStage;
  /** MANUAL: a person set it; AUTO: the courier status reader did. */
  stageSource: "MANUAL" | "AUTO" | null;
  stageAt: number | null;
  stageByName: string | null;
  /** yyyy-MM-dd */
  deliveredOn: string | null;
  overdue: boolean;
  /** Days after booking before the server flags it (config, default 15). */
  overdueDays?: number;
  hasSlipPhoto: boolean;
  createdAt: number;
  createdByName: string;
  version: number;
  /**
   * Courier + AWB line in the memo's Zoho invoice / delivery challan notes. Absent while that sync is off.
   * NOT_LINKED: the memo has no Zoho document yet.
   */
  zohoSyncState?: ZohoSyncState | null;
  zohoSyncedAt?: number | null;
  /** HKP's own short reason when FAILED. */
  zohoSyncError?: string | null;
  /** The courier has an automatic status reader switched on. False everywhere until one ships. */
  autoCheck?: boolean;
  /** Last automatic check: OK, or FAILED ("Couldn't check"); null before the first. A failure never changes the stage. */
  checkState?: "OK" | "FAILED" | null;
  lastCheckedAt?: number | null;
  /** The reader's own status label, e.g. "In Transit". */
  courierStatusText?: string | null;
};

export type ZohoSyncState = "SYNCED" | "FAILED" | "PENDING" | "NOT_LINKED";

export type ParcelEvent = {
  kind: "ADDED" | "EDITED" | "REMOVED" | "STAGE_MANUAL" | "STAGE_AUTO" | "CHECK_FAILED" | "ZOHO_PUSHED";
  fromStage: ParcelStage | null;
  toStage: ParcelStage | null;
  statusText: string | null;
  actorName: string | null;
  reason: string | null;
  at: number;
};

export type DispatchCourier = {
  id: string; name: string; trackingUrlTemplate: string | null; archived: boolean; lastUsedAt: number | null;
  /** NONE or the status reader id (set by the studio admin elsewhere; no UI here). */
  reader?: string;
  autoCheck?: boolean;
};

/** `slipPhotoUrl` is a signed link valid for 10 minutes; only this call returns it. */
export type ParcelDetail = { parcel: ParcelView; slipPhotoUrl: string | null; events: ParcelEvent[] };
export type AddParcelResult = { parcel: ParcelView; duplicateOf: string[] };
export type ParcelPage = { items: ParcelView[]; total: number; offset: number; limit: number };
export type SlipUpload = { uploadUrl: string; contentType: string; expiresInMinutes: number };
export type SlipFileType = "JPG" | "PNG" | "HEIC";

/** A saved courier by id, or a courier name (found, or added with its tracking link template). */
export type ParcelCourierInput = { courierId: string } | { courierName: string; trackingUrlTemplate?: string };

const parcelBase = `${base}/studio/sales`;
const call = <T>(request: Promise<unknown>) => request as Promise<SalesApiResult<T>>;

export const listMemoParcels = (memoId: string) =>
  call<ParcelView[]>(getApi(`${parcelBase}/memos/${encodeURIComponent(memoId)}/parcels`));

export const addMemoParcel = (memoId: string, body: ParcelCourierInput & { awbNumber: string; bookedOn?: string }) =>
  call<AddParcelResult>(postApi(`${parcelBase}/memos/${encodeURIComponent(memoId)}/parcels`, body));

export const getParcel = (id: string) => call<ParcelDetail>(getApi(`${parcelBase}/parcels/${encodeURIComponent(id)}`));

export const editParcel = (id: string, body: Partial<ParcelCourierInput> & { version: number; awbNumber?: string; bookedOn?: string }) =>
  call<ParcelView>(patchApi(`${parcelBase}/parcels/${encodeURIComponent(id)}`, body));

export const setParcelStage = (id: string, body: { stage: ParcelStage; date?: string; reason?: string }) =>
  call<ParcelView>(postApi(`${parcelBase}/parcels/${encodeURIComponent(id)}/stage`, body));

export const removeParcel = (id: string, reason: string) =>
  call<{ id: string; removed: boolean }>(deleteApi(`${parcelBase}/parcels/${encodeURIComponent(id)}`, { reason }));

/** Retry: writes the memo's parcels into its Zoho invoice or delivery challan notes now. */
export const syncParcelZoho = (id: string) =>
  call<ParcelView>(postApi(`${parcelBase}/parcels/${encodeURIComponent(id)}/zoho-sync`, {}));

export const listOverdueParcels = (offset = 0, limit = 20) =>
  call<ParcelPage>(getApi(`${parcelBase}/parcels?flag=overdue&offset=${offset}&limit=${limit}`));

export const startParcelSlipUpload = (id: string, fileType: SlipFileType) =>
  call<SlipUpload>(postApi(`${parcelBase}/parcels/${encodeURIComponent(id)}/slip-photo/upload`, { fileType }));

export const completeParcelSlipUpload = (id: string) =>
  call<ParcelDetail>(postApi(`${parcelBase}/parcels/${encodeURIComponent(id)}/slip-photo/complete`, {}));

export const listDispatchCouriers = () => call<DispatchCourier[]>(getApi(`${parcelBase}/dispatch/couriers`));

/** Change a saved courier's tracking link (used for parcels added from now on). Empty removes it. */
export const updateDispatchCourier = (id: string, body: { trackingUrlTemplate?: string; name?: string }) =>
  call<DispatchCourier>(putApi(`${parcelBase}/dispatch/couriers/${encodeURIComponent(id)}`, body));

/** "Check now": asks the courier once for this parcel (at most hourly; 429 says when to try again). */
export const checkParcelNow = (id: string) =>
  call<ParcelView>(postApi(`${parcelBase}/parcels/${encodeURIComponent(id)}/check`, {}));

// ---- Memo to studio order link --------------------------------------------------------------------------------

export type MemoOrderLink = { memoId: string; memoCode: string; studioOrderId: string | null; studioOrderCode: string | null };

/** Links the memo to an order of the studio, or unlinks it with null. */
export const linkMemoOrder = (memoId: string, studioOrderId: string | null) =>
  call<MemoOrderLink>(putApi(`${parcelBase}/memos/${encodeURIComponent(memoId)}/order`, { studioOrderId }));

/** A parcel as the order page shows it: no slip photo. */
export type OrderParcel = {
  id: string; courierName: string; awbNumber: string; trackingUrl: string | null; bookedOn: string;
  stage: ParcelStage; deliveredOn: string | null; overdue: boolean;
};
export type OrderMemo = {
  id: string; memoCode: string; type: "SALE" | "APPROVAL"; status: string; invoiceNumber: string | null; parcels: OrderParcel[];
};
export type OrderMemos = { orderId: string; orderCode: string; memos: OrderMemo[]; truncated: boolean };

export const getOrderMemos = (orderId: string) =>
  call<OrderMemos>(getApi(`${parcelBase}/orders/${encodeURIComponent(orderId)}/memos`));
